import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCodeEditorModule } from 'ng-zorro-antd/code-editor';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { LogTableComponent } from '../log-table.component';

@Component({
  selector: 'app-log-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NzSpinModule,
    NzCardModule,
    NzBreadCrumbModule,
    NzButtonModule,
    NzIconModule,
    NzGridModule,
    NzTagModule,
    NzTableModule,
    NzEmptyModule,
    NzTabsModule,
    NzCodeEditorModule,
    NzCollapseModule,
    NzDividerModule,
    LogTableComponent,
    I18nPipe,
    NzToolTipModule,
    TestIdDirective
  ],
  templateUrl: './log-detail.component.html',
  styleUrls: ['./log-detail.component.less']
})
export class LogDetailComponent implements OnInit {
  id: string = '';
  logDetail: any = null;
  loading: boolean = true;
  relatedItems: any[] = [];
  requestHeaderDecoded: boolean = false;
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly notification = inject(NzNotificationService);

  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  constructor(private readonly route: ActivatedRoute) {}

  // 添加分页相关属性
  relatedItemsLoading: boolean = false;
  relatedItemsTotal: number = 0;
  relatedItemsPageSize: number = 10;
  relatedItemsPageIndex: number = 1;

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.id = params.get('id') ?? '';
      if (this.id) {
        this.fetchLogDetail();
      } else {
        this.loading = false;
      }
    });
  }

  /**
   * 获取日志详情
   */
  fetchLogDetail(): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.ADMIN_LOG_V1_ITEM}/${this.id}`, {
        a: this.id
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.logDetail = res.data;
          this.requestHeaderDecoded = false;
          if (this.logDetail.traceId) {
            this.fetchRelatedItems(this.logDetail);
          }

          this.cdr.detectChanges();
        }
      });
  }
  fetchRelatedItems(item: any): void {
    const traceId = item.traceId;
    this.relatedItemsLoading = true;
    this.http
      .post(ConstantUtil.ADMIN_LOG_RELATED_ITEMS, {
        traceId: traceId,
        requestPath: item.requestPath,
        pageIndex: this.relatedItemsPageIndex - 1,
        pageSize: this.relatedItemsPageSize,
        direction: 'asc'
      })
      .pipe(
        finalize(() => {
          this.relatedItemsLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.relatedItems = res.data.items || [];
          if (this.relatedItemsPageIndex == 1) {
            this.relatedItemsTotal = res.data.totalCount;
          }
          this.cdr.detectChanges();
        }
      });
  }

  onRelatedItemsPageIndexChange(pageIndex: number): void {
    this.relatedItemsPageIndex = pageIndex;
    this.fetchRelatedItems(this.logDetail);
  }

  onRelatedItemsPageSizeChange(pageSize: number): void {
    this.relatedItemsPageSize = pageSize;
    this.relatedItemsPageIndex = 1;
    this.fetchRelatedItems(this.logDetail);
  }

  /**
   * 返回列表页
   */
  goBack(): void {
    this.router.navigate(['/logs']);
  }

  /**
   * 格式化JSON字符串
   */
  formatJson(json: string | object, decode: boolean = false): string {
    if (!json) return '';

    try {
      if (typeof json === 'string') {
        const parsed = JSON.parse(json);
        return JSON.stringify(decode ? this.decodeAndParseHeaderValue(parsed) : parsed, null, 2);
      } else {
        return JSON.stringify(decode ? this.decodeHeaderValue(json) : json, null, 2);
      }
    } catch (e) {
      if (decode && typeof json === 'string') {
        const decoded = this.decodeHeaderString(json);
        try {
          return JSON.stringify(this.decodeHeaderValue(JSON.parse(decoded)), null, 2);
        } catch {
          const mapValue = this.parseMapHeader(decoded);
          if (mapValue !== null) {
            return JSON.stringify(mapValue, null, 2);
          }
          return this.formatDecodedHeaderString(decoded);
        }
      }

      return typeof json === 'string' ? json : JSON.stringify(json);
    }
  }

  toggleRequestHeaderDecoded(): void {
    this.requestHeaderDecoded = !this.requestHeaderDecoded;
  }

  private decodeHeaderValue(value: unknown): unknown {
    if (typeof value === 'string') return this.decodeHeaderString(value);
    if (Array.isArray(value)) return value.map(item => this.decodeHeaderValue(item));
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, this.decodeHeaderValue(item)]));
    }
    return value;
  }

  private decodeAndParseHeaderValue(value: unknown): unknown {
    const decoded = this.decodeHeaderValue(value);
    if (typeof decoded !== 'string') return decoded;

    try {
      return this.decodeHeaderValue(JSON.parse(decoded));
    } catch {
      return decoded;
    }
  }

  private decodeHeaderString(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private formatDecodedHeaderString(value: string): string {
    const trimmed = value.trim();
    if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) return value;

    const content = trimmed.slice(1, -1);
    const formatted = content.replace(/,\s+(?=[!#$%&'*+.^_`|~\w-]+:)/g, ',\n  ');
    return formatted === content ? value : `[\n  ${formatted}\n]`;
  }

  private parseMapHeader(value: string): Record<string, unknown> | null {
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;

    const entries = this.splitTopLevel(trimmed.slice(1, -1));
    if (!entries.length) return {};

    const result: Record<string, unknown> = {};
    for (const entry of entries) {
      const separator = entry.indexOf('=');
      if (separator <= 0) return null;

      const key = entry.slice(0, separator).trim();
      const rawValue = entry.slice(separator + 1).trim();
      if (!key || !rawValue) return null;

      if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
        result[key] = this.splitTopLevel(rawValue.slice(1, -1)).map(item => this.parseMapHeaderValue(item));
      } else {
        result[key] = this.parseMapHeaderValue(rawValue);
      }
    }

    return result;
  }

  private parseMapHeaderValue(value: string): unknown {
    const candidate = value.trim();
    if (!candidate) return '';
    try {
      return JSON.parse(candidate);
    } catch {
      return this.decodeHeaderString(candidate);
    }
  }

  private splitTopLevel(value: string): string[] {
    const parts: string[] = [];
    let start = 0;
    let depth = 0;
    let quote: string | null = null;
    let escaped = false;

    for (let index = 0; index < value.length; index++) {
      const character = value[index];
      if (quote) {
        if (escaped) escaped = false;
        else if (character === '\\') escaped = true;
        else if (character === quote) quote = null;
        continue;
      }
      if (character === '"' || character === "'") {
        quote = character;
      } else if (character === '[' || character === '{' || character === '(') {
        depth++;
      } else if (character === ']' || character === '}' || character === ')') {
        depth--;
      } else if (character === ',' && depth === 0) {
        parts.push(value.slice(start, index).trim());
        start = index + 1;
      }
    }

    parts.push(value.slice(start).trim());
    return parts.filter(Boolean);
  }

  /**
   * 格式化字节大小
   */
  formatBytes(bytes: number): string {
    if (bytes === 0 || !bytes) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  copyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = typeof text === 'string' ? text : JSON.stringify(text, null, 2);
    document.body.appendChild(textArea);
    textArea.select();
    navigator.clipboard.writeText(text);
    document.body.removeChild(textArea);

    this.notification.create('success', '', this.i18n.fanyi('log.copy-success'));
  }
  viewRelatedItem(item: any): void {
    // 使用Location服务生成完整的URL
    const currentUrl = this.location.prepareExternalUrl(`/logs/detail/${item.id}`);

    // 确保URL包含适当的哈希前缀
    const fullUrl = window.location.origin + currentUrl;

    // 在新标签页中打开
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }

  loadChildItems(item: any): void {
    if (item.expanded) {
      item.expanded = false;
      return;
    }

    item.loading = true;

    this.http
      .post(ConstantUtil.ADMIN_LOG_RELATED_ITEMS, {
        traceId: item.traceId,
        requestPath: item.requestPath,
        pageIndex: this.relatedItemsPageIndex - 1,
        pageSize: this.relatedItemsPageSize,
        direction: 'asc'
      })
      .pipe(
        finalize(() => {
          item.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            // 为子项添加层级信息
            const children = res.data.items.map((child: any) => ({
              ...child,
              level: (item.level || 0) + 1,
              parentId: item.id
            }));

            // 更新当前项的子项数据
            item.children = children;
            item.expanded = true;
            this.cdr.detectChanges();
          } else {
            this.message.error(res.msg ?? this.i18n.fanyi('log.load-child-items-failed'));
          }
        },
        error: () => {
          this.message.error(this.i18n.fanyi('log.load-child-items-failed-retry'));
        }
      });
  }
}
